import React, { useEffect, useState } from "react";
import { Card, Dropdown, Image } from "react-bootstrap";
import Skeleton from "react-loading-skeleton";
import { useNavigate, useLocation } from "react-router-dom";
import { ChangeExerciseLibraryStatus } from "services/Admin/UserManagement/Exercise.service";
import { toast } from "react-toastify";
import {
  faBookOpen,
  faCalendarDays,
  faCircleCheck,
  faClipboardList,
  faClock,
  faEdit,
  faEye,
  faUsers,
  faFile,
  faFont,
  faHeadphones,
  faMicrophone,
  faPenNib,
  faPlus,
  faTrash,
  faUserGroup,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import nodataimage from "../../../assets/nodataimage.svg";
import ExerciseListPage from "components/shared/ExerciseListPage";
import ReasonModal from "components/modals/ReasonModal";
import DeleteAssignmentModal from "components/modals/DeleteTeacherAssignmentModal";
import {
  deleteAssignments,
  getAllAssignment,
} from "services/Teacher/Assignments.service";
import moment from "moment";
import dropdown from "../../../assets/dropdown.svg";
import CapitalizeFirst from "components/shared/CapitalizeFirst";
import ViewStudentsModal from "components/modals/ViewStudentsModal";
import UpdateAssignmentModal from "components/modals/UpdateAssignmentModal";
import { useTranslation } from "react-i18next";

const typeOptions = ["All", "reading", "writing", "listening"]
const assignOptions = ["All", "class", "individual"]
const sortOptions = ["Latest", "Oldest", "A to Z", "Z to A"]

// Lifecycle status pills; keys match the `status` the API derives per assignment.
const STATUS_STYLES = {
  assigned: { label: "Assigned", bg: "#E6F7EF", color: "#1B9C63" },
  "in-progress": { label: "In progress", bg: "#E6F7EF", color: "#1B9C63" },
  "not-started": { label: "Not started", bg: "#FFF3E2", color: "#D9822B" },
  completed: { label: "Completed", bg: "#E6F7EF", color: "#1B9C63" },
  overdue: { label: "Overdue", bg: "#FDE8EC", color: "#E4415E" },
  draft: { label: "Draft", bg: "#EFEFF4", color: "#6B7280" },
}

const TeacherAssignments = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const searchParams = new URLSearchParams(location.search);
  const initialPage = parseInt(searchParams.get("page"), 10) || 1;
  // Larger page size than the old pager, so each infinite-scroll fetch is worthwhile.
  const initialLimit = parseInt(searchParams.get("limit"), 10) || 15;

  const [exercises, setExercises] = useState([]);
  const [overview, setOverview] = useState(null);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [currentPage, setCurrentPage] = useState(initialPage);
  const [totalPages, setTotalPages] = useState(1);
  const [showModal, setShowModal] = useState(false);
  const [showEye, setShowEye] = useState(false);
  const [eyeAssignmentId, setEyeAssignmentId] = useState(null);
  const [eyeAssignmentTitle, setEyeAssignmentTitle] = useState("");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [pendingStatusChange, setPendingStatusChange] = useState(null);
  const [modalVisible, setModalVisible] = useState(false); // Modal visibility state
  const [selectedAssignment, setSelectedAssignment] = useState(null); // Store selected assignment details

  const [selectedAssign, setSelectedAssign] = useState(null);
  const [selectedType, setSelectedType] = useState(null);
  const [selectedSort, setSelectedSort] = useState(null);

  const limit = initialLimit;

  // Page 1 replaces the list (fresh load / filter change); later pages append,
  // which turns the pager into an infinite scroll.
  const AllExercises = async (page = 1, limit = 15, type = "", assign = "", sort = "", search = "") => {
    const isFirstPage = page === 1;
    if (isFirstPage) {
      setLoading(true);
    } else {
      setLoadingMore(true);
    }
    try {
      const response = await getAllAssignment(page, limit, type, assign, sort, search);
      if (response.data && response.data.results) {
        const incoming = response.data.results.assignments || [];
        setExercises((prev) => {
          if (isFirstPage) return incoming;
          // Guard against duplicates if a page is fetched twice.
          const seen = new Set(prev.map((a) => a._id));
          return [...prev, ...incoming.filter((a) => !seen.has(a._id))];
        });
        // Overview reflects the whole set, so only refresh it on the first page.
        if (isFirstPage) setOverview(response.data.results.overview || null);
        setTotalPages(response.data.results.totalPages || 1);
        setCurrentPage(page);
        setSelectedType(type);
        setSelectedAssign(assign)
        setSelectedSort(sort);
        setSearch(search)
      } else if (isFirstPage) {
        setExercises([]);
        setTotalPages(1);
      }
    } catch (error) {
      console.error("Error fetching exercises:", error);
      if (isFirstPage) {
        setExercises([]);
        setTotalPages(1);
      }
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const hasMore = currentPage < totalPages;

  const loadMore = () => {
    if (loading || loadingMore || !hasMore) return;
    AllExercises(currentPage + 1, limit, selectedType, selectedAssign, selectedSort, search);
  };

  useEffect(() => {
    AllExercises(1, limit, selectedType, selectedAssign, selectedSort, search);
  }, []);


  const updateStatus = async (id, newStatus, reason = "") => {
    const toastId = toast.loading(t("Updating status..."));
    try {
      await ChangeExerciseLibraryStatus(id, {
        status: newStatus,
        message: reason,
      });
      AllExercises(1, limit, selectedType, selectedAssign, selectedSort, search);
      toast.update(toastId, {
        render: t("Status updated successfully!"),
        type: "success",
        isLoading: false,
        autoClose: 3000,
        closeOnClick: true,
      });
    } catch (error) {
      toast.update(toastId, {
        render: t("Error updating status"),
        type: "error",
        isLoading: false,
        autoClose: 3000,
        closeOnClick: true,
      });
      console.error("Error updating status:", error);
    }
  };

  const handleSaveReason = (reason) => {
    if (pendingStatusChange) {
      updateStatus(
        pendingStatusChange.id,
        pendingStatusChange.newStatus,
        reason
      );
    }
    setShowModal(false);
    setPendingStatusChange(null);
  };

  const handleShowDeleteModal = (id) => {
    setPendingStatusChange({ id, newStatus: "deleted" });
    setShowDeleteModal(true);
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
  };

  const handleDeleteExercise = async (id) => {
    const toastId = toast.loading(t("Deleting exercise..."));
    try {
      await deleteAssignments(id);
      AllExercises(1, limit, selectedType, selectedAssign, selectedSort, search);
      toast.update(toastId, {
        render: t("Assignment deleted successfully!"),
        type: "success",
        isLoading: false,
        autoClose: 3000,
        closeOnClick: true,
      });
    } catch (error) {
      toast.update(toastId, {
        render: t("Error deleting exercise"),
        type: "error",
        isLoading: false,
        autoClose: 3000,
        closeOnClick: true,
      });
      console.error("Error deleting exercise:", error);
    } finally {
      setShowDeleteModal(false);
      setPendingStatusChange(null);
    }
  };

  // The modal loads the full roster (class assignments don't carry one on the
  // row), so it only needs the assignment id plus whatever we already have.
  const handleShowEyeModal = (item) => {
    setEyeAssignmentId(item?._id || null);
    setEyeAssignmentTitle(item?.title || "");
    setShowEye(true);
  };

  const handleCloseEyeModal = () => {
    setShowEye(false);
  };
  const hideModal = () => {
    setModalVisible(false);
    // fetchAssignments();
  };
  const UpdateAssignment = () => {
    setModalVisible(false);
    // fetchAssignments();
    AllExercises(1, limit, selectedType, selectedAssign, selectedSort, search);
  };
  const handleEditModal = (exerciseId) => {
    setSelectedAssignment(exerciseId);
    setModalVisible(true);
  };

  // Submissions live under the assignments route now that the standalone
  // Submissions page is gone.
  const handleViewSubmissions = (item) =>
    navigate(
      item?.type === "exercise"
        ? `/assignments/submissions/view-all/${item._id}`
        : `/assignments/submissions/vocabulary/${item._id}`
    );

  // Opens the source exercise/studyset the assignment was created from.
  const handleShowSource = (item) =>
    item?.type === "exercise"
      ? navigate(`/exercise/my-exercises/${item.exercise._id}`)
      : navigate(`/vocabulary/${item.studySet._id}`);

  const getExerciseType = (item) =>
    item?.type === "exercise"
      ? (item?.exercise?.exerciseType || "").toLowerCase()
      : "vocabulary";

  const TYPE_STYLES = {
    writing: { bg: "#EFE9FE", color: "#7A4DE8", icon: faPenNib },
    reading: { bg: "#E7F0FE", color: "#2F6FE4", icon: faBookOpen },
    vocabulary: { bg: "#FEEFE3", color: "#E1761F", icon: faFont },
    listening: { bg: "#E6F7F1", color: "#159C6B", icon: faHeadphones },
    speaking: { bg: "#FDE8EC", color: "#E4415E", icon: faMicrophone },
  };

  const getTypeStyle = (type) =>
    TYPE_STYLES[type] || { bg: "#EFEFEF", color: "#6B7280", icon: faFile };

  const columns = [
    {
      key: "title",
      label: "Assignment",
      thClassName: "py-3 ps-3 fw-semibold",
      tdClassName: "py-3 ps-3",
      skeleton: <Skeleton width="70%" />,
      render: (item) => {
        const type = getExerciseType(item);
        const style = getTypeStyle(type);
        return (
          <div className="d-flex align-items-center gap-2">
            <div
              className="exercise-thumb"
              style={{ background: style.bg, color: style.color }}
            >
              <FontAwesomeIcon icon={style.icon} />
            </div>
            <div>
              <div className="fw-bold" style={{ fontSize: "14px" }}>
                {item?.title}
              </div>
              <div className="text-muted" style={{ fontSize: "12px" }}>
                <CapitalizeFirst text={type} /> {t("exercise")}
              </div>
            </div>
          </div>
        );
      },
    },
    {
      key: "type",
      label: "Type",
      skeleton: <Skeleton width={70} />,
      render: (item) => {
        const type = getExerciseType(item);
        const style = getTypeStyle(type);
        return (
          <span
            style={{
              display: "inline-block",
              padding: "4px 12px",
              borderRadius: "8px",
              fontSize: "12px",
              fontWeight: 600,
              backgroundColor: style.bg,
              color: style.color,
              textTransform: "capitalize",
            }}
          >
            {type}
          </span>
        );
      },
    },
    {
      key: "assignedTo",
      label: "Assigned to",
      skeleton: <Skeleton width={90} />,
      render: (item) => {
        // Individual assignments know their count up front; class ones
        // only have the expected roster size from submissionStats.
        const count =
          item?.selectedStudents?.length || item?.submissionStats?.expected || 0;
        return (
          <div style={{ fontSize: "14px", fontWeight: 600 }}>
            {count > 0 ? `${count} ${t("students")}` : "—"}
          </div>
        );
      },
    },
    {
      key: "deadline",
      label: "Deadline",
      skeleton: <Skeleton width={70} />,
      render: (item) => {
        if (!item?.endDate) {
          return (
            <span className="text-muted" style={{ fontSize: "13px" }}>
              {t("No deadline")}
            </span>
          );
        }
        const end = moment(item.endDate);
        const overdue = end.isBefore(moment(), "day");
        return (
          <div style={{ whiteSpace: "nowrap" }}>
            <div
              className="d-inline-flex align-items-center gap-1"
              style={{ fontSize: "14px", fontWeight: 600 }}
            >
              <FontAwesomeIcon
                icon={faCalendarDays}
                className="text-muted"
                style={{ fontSize: "12px" }}
              />
              {end.format("DD MMM YYYY")}
            </div>
            <div
              style={{ fontSize: "12px", color: overdue ? "#E4553F" : "#8c8c8c" }}
            >
              {end.fromNow()}
            </div>
          </div>
        );
      },
    },
    {
      key: "status",
      label: "Status",
      skeleton: <Skeleton width={70} />,
      render: (item) => {
        const style = STATUS_STYLES[item?.status] || STATUS_STYLES.assigned;
        return (
          <span
            style={{
              display: "inline-block",
              padding: "4px 12px",
              borderRadius: "8px",
              fontSize: "12px",
              fontWeight: 600,
              whiteSpace: "nowrap",
              backgroundColor: style.bg,
              color: style.color,
            }}
          >
            {t(style.label)}
          </span>
        );
      },
    },
    {
      key: "submissions",
      label: "Submissions",
      skeleton: <Skeleton width={90} />,
      render: (item) => {
        const stats = item?.submissionStats || {};
        const submitted = stats.submitted || 0;
        const expected = stats.expected || 0;
        const percent = expected
          ? Math.round((submitted / expected) * 100)
          : 0;
        return (
          <div style={{ minWidth: "130px" }}>
            <div style={{ fontSize: "13px", marginBottom: "4px" }}>
              <span style={{ fontWeight: 700, color: "#303972" }}>
                {submitted}
              </span>
              <span className="text-muted"> / {expected}</span>
            </div>
            <div
              className="d-flex align-items-center gap-2"
              title={`${submitted} ${t("of")} ${expected} ${t("submitted")}`}
            >
              <div
                style={{
                  flex: 1,
                  height: "6px",
                  borderRadius: "10px",
                  backgroundColor: "#EDEDF2",
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    width: `${Math.min(percent, 100)}%`,
                    height: "100%",
                    borderRadius: "10px",
                    backgroundColor: "#22B573",
                  }}
                />
              </div>
              <span
                className="text-muted"
                style={{ fontSize: "12px", whiteSpace: "nowrap" }}
              >
                {percent}%
              </span>
            </div>
          </div>
        );
      },
    },
    {
      // Replaces the old "Max time" column — how much marking is still owed is
      // far more actionable to a teacher scanning this list.
      key: "unmarked",
      label: "Unmarked",
      skeleton: <Skeleton width={55} />,
      tdClassName: "py-3",
      tdStyle: { whiteSpace: "nowrap" },
      render: (item) => {
        const stats = item?.submissionStats || {};
        const unmarked = stats.awaitingGrading || 0;
        // Nothing handed in yet is not the same as "all marked" — saying the
        // latter sends teachers to an empty submissions list.
        if (!unmarked && !(stats.submitted || 0)) {
          return (
            <span className="text-muted" style={{ fontSize: "13px" }}>
              {t("No submissions")}
            </span>
          );
        }
        return (
          <span
            style={{
              display: "inline-block",
              padding: "4px 12px",
              borderRadius: "8px",
              fontSize: "12px",
              fontWeight: 600,
              backgroundColor: unmarked ? "#FFF3E2" : "#EFEFF4",
              color: unmarked ? "#D9822B" : "#6B7280",
            }}
            title={
              unmarked
                ? `${unmarked} ${t("submissions awaiting grading")}`
                : t("Nothing to mark")
            }
          >
            {unmarked ? `${unmarked} ${t("to mark")}` : t("All marked")}
          </span>
        );
      },
    },
  ];

  const renderActions = (item) => (
    <div onClick={(e) => e.stopPropagation()}>
      <Dropdown align="end">
        <Dropdown.Toggle
          className="more bg-transparent text-black border-0 e-caret-hide rounded-0"
          id="dropdown-basic"
        >
          <Card.Img
            style={{ width: "24px", height: "24px" }}
            variant="top"
            src={dropdown}
          />
        </Dropdown.Toggle>
        <Dropdown.Menu
          className="border-0"
          style={{ boxShadow: "rgba(149, 157, 165, 0.2) 0px 8px 24px" }}
        >
          <Dropdown.Item
            style={{ color: "black" }}
            onClick={() => handleViewSubmissions(item)}
          >
            <FontAwesomeIcon icon={faFile} className="me-2" />
            {t("View Submissions")}
          </Dropdown.Item>
          <Dropdown.Item
            style={{ color: "black" }}
            onClick={() => handleShowEyeModal(item)}
          >
            <FontAwesomeIcon icon={faUsers} className="me-2" />
            {t("View assigned students")}
          </Dropdown.Item>
          {item?.type === "exercise" && (
            <Dropdown.Item
              style={{ color: "black" }}
              onClick={() => handleEditModal(item._id)}
            >
              <FontAwesomeIcon icon={faEdit} className="me-2" />
              {t("Edit Assignment")}
            </Dropdown.Item>
          )}
          <Dropdown.Item
            onClick={() => handleShowSource(item)}
            style={{ color: "black" }}
          >
            <FontAwesomeIcon icon={faEye} className="me-2" />
            {item?.type === "exercise"
              ? t("Show exercise")
              : t("Show studyset")}
          </Dropdown.Item>
          <Dropdown.Item
            className="text-danger"
            onClick={() => handleShowDeleteModal(item._id)}
          >
            <FontAwesomeIcon icon={faTrash} className="me-2" />
            {t("Delete")}
          </Dropdown.Item>
        </Dropdown.Menu>
      </Dropdown>
    </div>
  );

  const overviewCards = [
    {
      key: "totalAssignments",
      label: "Total assignments",
      icon: faClipboardList,
      color: "#4F5BD5",
      bg: "#EEF0FF",
    },
    {
      key: "totalStudents",
      label: "Students",
      icon: faUserGroup,
      color: "#2F6FE4",
      bg: "#E7F0FE",
    },
    {
      key: "totalSubmitted",
      label: "Submitted",
      icon: faCircleCheck,
      color: "#1B9C63",
      bg: "#E6F7EF",
    },
    {
      key: "awaitingGrading",
      label: "Awaiting grading",
      icon: faClock,
      color: "#D9822B",
      bg: "#FFF3E2",
    },
  ];

  const overviewSection = (
    <div className="assignments-overview">
      <div className="assignments-overview-main">
        <div className="assignments-overview-intro">
          <h6>{t("Overview")}</h6>
          <p>{t("Quick summary of your assignments.")}</p>
        </div>
        <div className="assignments-overview-cards">
          {overviewCards.map((card) => (
            <div key={card.key} className="assignments-overview-card">
              <span
                className="assignments-overview-icon"
                style={{ background: card.bg, color: card.color }}
              >
                <FontAwesomeIcon icon={card.icon} />
              </span>
              <div>
                <div className="assignments-overview-value">
                  {loading ? (
                    <Skeleton width={30} />
                  ) : (
                    overview?.[card.key] ?? 0
                  )}
                </div>
                <div className="assignments-overview-label">
                  {t(card.label)}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const pillToggle = (id, current, placeholder, options, onSelect) => (
    <Dropdown>
      <Dropdown.Toggle variant="light" id={id}>
        {t(current) || t(placeholder)}
      </Dropdown.Toggle>
      <Dropdown.Menu>
        {options.map((item) => (
          <Dropdown.Item
            key={item}
            onClick={() => onSelect(item)}
            style={{ textTransform: "capitalize" }}
          >
            {t(item)}
          </Dropdown.Item>
        ))}
      </Dropdown.Menu>
    </Dropdown>
  );

  return (
    <>
      <ReasonModal
        show={showModal}
        onClose={() => setShowModal(false)}
        onSave={handleSaveReason}
      />

      {overviewSection}

      <ExerciseListPage
        heading={"Assignments"}
        subtitle="View and manage all your assignments and student submissions in one place."
        searchPlaceholder="Search assignments..."
        headerExtra={
          <button
            type="button"
            className="exercise-create-btn solid"
            onClick={() => navigate("/exercise/my-exercises")}
          >
            <FontAwesomeIcon icon={faPlus} className="me-2" />
            {t("Create Assignment")}
          </button>
        }
        exercises={exercises}
        loading={loading}
        emptyState={
          <>
            <Image src={nodataimage} />
            <h5 className="mt-2 text-secondary">{t("No assignments found")}</h5>
          </>
        }
        typeTabs={[]}
        filterExtra={
          <>
            {pillToggle(
              "assignments-type",
              selectedType,
              "Filter by type",
              typeOptions,
              (value) =>
                AllExercises(1, limit, value, selectedAssign, selectedSort, search)
            )}
            {pillToggle(
              "assignments-assigned",
              selectedAssign,
              "Assigned to",
              assignOptions,
              (value) =>
                AllExercises(1, limit, selectedType, value, selectedSort, search)
            )}
          </>
        }
        sortOptions={sortOptions}
        selectedSort={selectedSort}
        search={search}
        onSearchChange={setSearch}
        onSortChange={(value) =>
          AllExercises(1, limit, selectedType, selectedAssign, value, search)
        }
        onSearchSubmit={() =>
          AllExercises(1, limit, selectedType, selectedAssign, selectedSort, search)
        }
        columns={columns}
        onRowClick={handleViewSubmissions}
        renderActions={renderActions}
        infiniteScroll
        hasMore={hasMore}
        loadingMore={loadingMore}
        onLoadMore={loadMore}
      />

      <DeleteAssignmentModal
        show={showDeleteModal}
        handleClose={handleCloseDeleteModal}
        handleConfirmDelete={() => handleDeleteExercise(pendingStatusChange.id)}
      />
      <ViewStudentsModal
        show={showEye}
        onClose={handleCloseEyeModal}
        assignmentId={eyeAssignmentId}
        assignmentTitle={eyeAssignmentTitle}
      />
      {modalVisible && (
        <UpdateAssignmentModal
          show={modalVisible}
          onHide={hideModal}
          assignmentId={selectedAssignment}
          UpdateAssignment={UpdateAssignment}
        />
      )}
    </>
  );
};

export default TeacherAssignments;
